/** @jsxImportSource theme-ui */
import * as React from 'react';
import { Box } from 'theme-ui';
import { useTranslation } from 'react-i18next';
import {
  getLiveJoinErrorCode,
  useLiveSessions,
  useSplitChallenge,
  useVideoSessions,
} from '@youga/youga-client-api';
import i18n from '../../../i18n/i18n';
import Text from '../../01_atoms/Text/Text';
import VideoSessionItem from '../../01_atoms/VideoSessionItem/VideoSessionItem';
import { LiveTrainingCard } from '../LiveTrainings/LiveTrainings';
import useTracking from '../../../hooks/useTracking';

export default function SplitChallenge() {
  const { t } = useTranslation();
  const { data } = useSplitChallenge();
  const { data: videoData } = useVideoSessions(i18n.language);
  const {
    data: liveRows,
    joinedSessionIds,
    joinLiveSession,
    refreshBalance,
  } = useLiveSessions();
  const { track } = useTracking();
  const [error, setError] = React.useState('');
  if (!data?.enabled) return null;
  const item = data.today;
  const video =
    item?.contentType === 'video_session'
      ? videoData?.videoSessions[item.contentId]
      : undefined;
  const live =
    item?.contentType === 'live_session'
      ? liveRows.find((row) => row.id === item.contentId)
      : undefined;
  const onJoin = async (sessionId: string, isFree = false) => {
    if (
      !window.confirm(
        t(
          joinedSessionIds.includes(sessionId)
            ? 'LIVE_REJOIN_CONFIRM'
            : isFree
            ? 'LIVE_FREE_JOIN_CONFIRM'
            : 'LIVE_JOIN_CONFIRM',
        ),
      )
    )
      return;
    try {
      setError('');
      void track('live-join-confirmed', { sessionId, isFree });
      const { joinUrl } = await joinLiveSession(sessionId);
      await refreshBalance();
      window.open(joinUrl, '_blank', 'noopener,noreferrer');
      void track('live-meet-opened', { sessionId, provider: 'google_meet' });
    } catch (reason) {
      const code = getLiveJoinErrorCode(reason);
      setError(
        code === 'LIVE_CREDITS_REQUIRED'
          ? t('LIVE_NO_CREDITS')
          : code === 'LIVE_SESSION_FULL'
          ? t('LIVE_FULL')
          : t('LIVE_JOIN_ERROR'),
      );
    }
  };
  return (
    <Box as="section" sx={{ mt: [4, 6], mb: [5, 8] }}>
      <Text variant="h2light" sx={{ fontSize: [3, 4], mb: 1 }}>
        {t('SPLIT_CHALLENGE')}
      </Text>
      <Text variant="h6light" sx={{ mb: [3, 4] }}>
        {t('SPLIT_CHALLENGE_TODAY')}
      </Text>
      {live ? (
        <Box sx={{ maxWidth: '720px' }}>
          <LiveTrainingCard session={live} onJoin={onJoin} />
        </Box>
      ) : video ? (
        <Box sx={{ maxWidth: '720px' }}>
          <VideoSessionItem
            mode="horizontal"
            fullWidth
            target={`/app/session/${video.id}`}
            title={video.title}
            content={video.subtitle || video.description}
            thumbnailUrl={video.thumbnailSmall || video.thumbnail}
            thumbnailHqUrl={video.thumbnailSmallHq || video.headerImage}
            duration={video.duration}
            instructor={video.instructor}
            instructorId={video.instructor}
            hideInstructor={false}
          />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: '#fff2eb',
            borderRadius: '16px',
            p: 4,
            maxWidth: '720px',
          }}
        >
          <Text>{t('SPLIT_CHALLENGE_EMPTY')}</Text>
        </Box>
      )}
      {error && (
        <Text role="alert" sx={{ color: 'red', mt: 2 }}>
          {error}
        </Text>
      )}
    </Box>
  );
}
